import { type RefCallback, useCallback, useRef, useState } from 'react';
import { useSettings } from '@wu/widget-core';
import { isInlineLayout } from '../../../utils';
import { MEDIUM_BREAKPOINT, MOBILE_BREAKPOINT } from '../constants';
import { calculateBreakpointValue } from './calculate-breakpoint-value';

export const useBreakpoints = () => {
  const { layout } = useSettings();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const isInline = isInlineLayout(layout);

  const currentBreakpointsRef = useRef({
    [MOBILE_BREAKPOINT]: false,
    [MEDIUM_BREAKPOINT]: false
  });
  const [breakpoints, setBreakpoints] = useState({
    [MOBILE_BREAKPOINT]: false,
    [MEDIUM_BREAKPOINT]: false
  });

  const updateBreakpoints = useCallback(
    (width: number) => {
      setBreakpoints((previousBreakpoints) => {
        const newBreakpoints = {
          [MOBILE_BREAKPOINT]: calculateBreakpointValue({
            width,
            breakpoint: MOBILE_BREAKPOINT,
            previousBreakpointValue: previousBreakpoints[MOBILE_BREAKPOINT],
            withHysteresis: isInline
          }),
          [MEDIUM_BREAKPOINT]: calculateBreakpointValue({
            width,
            breakpoint: MEDIUM_BREAKPOINT,
            previousBreakpointValue: previousBreakpoints[MEDIUM_BREAKPOINT],
            withHysteresis: isInline
          })
        };
        currentBreakpointsRef.current = newBreakpoints;

        return newBreakpoints;
      });
    },
    [isInline]
  );

  const refCallback: RefCallback<HTMLDivElement> = useCallback(
    (node) => {
      resizeObserverRef.current?.disconnect();

      if (!node) {
        return;
      }

      updateBreakpoints(node.offsetWidth);

      resizeObserverRef.current = new ResizeObserver(([entry]) => {
        if (!isInline) {
          updateBreakpoints(window.innerWidth);

          return;
        }

        const {
          contentBoxSize: [{ inlineSize: width }]
        } = entry;
        updateBreakpoints(width);
      });

      resizeObserverRef.current.observe(node);
    },
    [updateBreakpoints, isInline]
  );

  return {
    ref: refCallback,
    breakpoints
  };
};
